"use client"
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import clsx from 'clsx';
import { z } from 'zod'
import { zodResolver } from "@hookform/resolvers/zod";

import { InfosStep } from './InfosStep';
import { CoordStep } from './CoordStep';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/stories/atoms/Button/Button';
import { Icon } from '@/stories/atoms/Icon/Icon';
import { Loader } from '@/stories/atoms/Loader/Loader';
import { type Option } from '@/stories/components/Form/CustomSelect/CustomSelect';

import styles from './estimation-form.module.css';
import tabStyles from '@/stories/components/Tabs/tab.module.css'
import tabsStyles from '@/stories/components/Tabs/tabs.module.css'
import { useEstimateFormStore } from '../../Search/SearchModule/useEstimateFormStore';
import { getConsentText } from '@/domains/form/form.utils';
import { getRoute } from '@/utils/utils';
import { CommuneEntity, getPostalLocality } from '@/utils/geo-search';
import { ErrorMessage } from '@/stories/components/Form/FormItem/ErrorMessage';
import { sendEstimForm } from '@/domains/form/form.api';

export type EstimationFormContentProps = {
  defaultLocation?: CommuneEntity,
  nom: string,
  agenceId: string,
  context?: 'rent' | 'sell' | 'global',
  typeBiens?: Option[],
}

export const EstimationForm = ({
  defaultLocation,
  nom,
  agenceId,
  context,
  typeBiens,
}: EstimationFormContentProps) => {
  const [send, setSend] = useState(false)
  const [loader, setLoader] = useState(false)
  // `persist` n'existe pas côté serveur : on n'y accède que dans des callbacks client
  const storeHydrated = useSyncExternalStore(
    (callback) => useEstimateFormStore.persist.onFinishHydration(callback),
    () => useEstimateFormStore.persist.hasHydrated(),
    () => false
  )
  const [activeStep, setActiveStep] = useState(0)
  const { locality, type } = useEstimateFormStore()
  const [message, setMessage] = useState(undefined)
  const [stepData, setStepData] = useState<Partial<z.infer<typeof step1Schema> & z.infer<typeof step2Schema>>>()

  const step1Schema = useMemo(() =>
    z.object({
      tb: z.string({ required_error: 'Type de bien est requis' }).min(1, 'Type de bien est requis'),
      address: z.string({ required_error: 'Adresse est requise' }).min(1, 'Adresse est requise'),
      locality: z.custom<CommuneEntity>(val => val !== null && val !== undefined && typeof val === 'object', { message: 'Commune est requise' }).nullable().refine(val => val !== null, { message: 'Commune est requise' }),
      nbRoom: z.coerce.number().optional(),
      surface: z.coerce.number().optional(),
      ...(context === 'global' ? { type: z.enum(['sell', 'rent'], { required_error: 'Type est requis' }) } : {}),
      comment: z.string().optional(),
    }), [context])

  const step2Schema = useMemo(() =>
    z.object({
      firstName: z.string({ required_error: 'Prénom est requis' }).min(1, 'Prénom est requis'),
      lastName: z.string({ required_error: 'Nom est requis' }).min(1, 'Nom est requis'),
      email: z.string({ required_error: 'Email est requis' }).email('Email invalide'),
      phone: z.string({ required_error: 'Téléphone est requis' }).min(1, 'Téléphone est requis'),
      consent: z.boolean({ required_error: 'Consentement obligatoire', invalid_type_error: 'Consentement obligatoire' }).refine(val => val === true, { message: 'Consentement obligatoire' }),
    }), [])

  useEffect(() => {
    useEstimateFormStore.persist.rehydrate()
  }, [])

  const steps = [
    {
      title: 'Informations sur votre bien',
      component: <InfosStep typeBiens={typeBiens ? typeBiens : [
        {label: 'Maison', value: 'Maison'},
        {label: 'Maison de village', value: 'Maison de village'},
        {label: 'Appartement', value: 'Appartement'},
        {label: 'Appartement Duplex', value: 'Appartement Duplex'},
        {label: 'Appartement Triplex', value: 'Appartement Triplex'},
        {label: 'Loft', value: 'Loft'},
        {label: 'Immeuble', value: 'Immeuble'},
        {label: 'Bâtiment', value: 'Bâtiment'},
        {label: 'Château', value: 'Château'},
        {label: 'Ferme', value: 'Ferme'},
        {label: 'Garages', value: 'Garages'},
        {label: 'Grange', value: 'Grange'},
        {label: 'Fond de commerce', value: 'Fond de commerce'},
        {label: 'Local commercial', value: 'Local commercial'},
        {label: 'Local industriel', value: 'Local industriel'},
        {label: 'Cave', value: 'Cave'},
        {label: 'Parking', value: 'Parking'},
        {label: 'Plateau', value: 'Plateau'},
        {label: 'Villa', value: 'house-chimney'},
        ...(context === 'sell' || context === 'global' ? [{label: 'Terrain', value: 'Terrain'}] : []),
        {label: 'Autres', value: 'Autres'}
      ] as Option[]} context={context} />
    },
    {
      title: 'Vos coordonnées',
      component: <CoordStep nom={nom}/>
    },
  ]

  const validationSchema = [step1Schema, step2Schema] as const

  const currentValidationSchema = validationSchema[activeStep];

  const methods = useForm({
    // @ts-expect-error : union de schemas par étape non typable précisément
    resolver: zodResolver(currentValidationSchema),
    mode: "onChange",
    defaultValues: {
      locality: (defaultLocation ? defaultLocation : locality) || undefined,
      ...(context === 'global' ? {type: type ? type : 'sell'} : {})
    }
  })

  const { handleSubmit } = methods

  const onSubmit = async (values: Partial<z.infer<typeof step1Schema> & z.infer<typeof step2Schema>>) => {
  
    if (activeStep === steps.length -1) {
      setLoader(true)
      setMessage(undefined)
      // envoie form
      const data = await sendEstimForm({
        message: stepData?.comment || '',
        agenceId: agenceId,
        vente: {
          statut: null,
          motif: null,
          // the type is only chosen by the user in the global context, otherwise it is the page context
          variant: (context === 'global' ? stepData?.type : context) === 'rent' ? 'location' : 'achatvente',
          adressePostale: stepData?.address || '',
          ...getPostalLocality(stepData?.locality),
          type: stepData?.tb || '',
          nbPieces: stepData?.nbRoom || 0,
          surface: stepData?.surface || 0
        },
        contact: {
          email: values.email || '',
          prenom: values.firstName || '',
          nom: values.lastName || '',
          telephone: values.phone || ''
        },
        consent: {
          privacyConsentGiven: values.consent || false,
          consentText: getConsentText(nom),
          privacyConsentAt: new Date().toISOString(),
          consentLanguage: 'fr',
          privacyPolicyUrl: getRoute({key: 'privacy-policy'}),
          privacyPolicyVersion: 'v1',
        }
      })
      setLoader(false)
      if (data.message) {
        setMessage(data.message)
        setActiveStep(0)
      } else {
        setSend(true)
      }
    } else {
      setStepData({...values, ...stepData})
      setActiveStep(activeStep + 1)
    }
  }

  if (loader || !storeHydrated) {
    return <div className={styles.loading}><Loader /></div>
  }

  return send ? (
    <>Votre formulaire a bien été envoyé 🙂</>
  ): (
    <FormProvider {...methods}>
      <div className={tabsStyles.wrapper}>
        {steps.map((step, i) => (
          <button
            type='button'
            key={step.title}
            className={clsx(tabStyles.button, activeStep === i && tabStyles.active)}
            {...(activeStep !== i &&i !== steps.length - 1 ? {onClick: () => setActiveStep(activeStep -1)} : {})}
            >
            {step.title}
          </button>
        ))}
      </div>
      {message && (<ErrorMessage>{message}</ErrorMessage>)}
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {steps.map((step, i) => i === activeStep ? <div key={i} className={styles.form}>{step.component}</div> : null)}
        <div className={styles.btns}>
          {activeStep > 0 ? <Button type='button' variant='secondary' onClick={() => setActiveStep(activeStep -1)}><Icon icon='arrow-left'/>Retour</Button> : null }
          {activeStep !== steps.length - 1 ? (
            <Button variant='secondary' type='submit' className={styles.next}>Continuer<Icon icon='arrow-right'/></Button>
          ) : (
            <Button variant='primary' type='submit' className={styles.next}>Envoyer<Icon icon='envelope'/></Button>
          )}
        </div>
      </form>
    </FormProvider>
  )
}